import { type RefObject, useEffect, useRef, useState } from 'react';

type CallbackType<T> = (
  triggeredRef: RefObject<T | null>,
  triggered: boolean
) => void;

interface Props<T> {
  callback?: CallbackType<T>;
  once?: boolean;
  options?: IntersectionObserverInit;
}

const useIntersection = <T extends Element | null>({
  callback = undefined,
  once = false,
  options = {},
}: Props<T>) => {
  const callbackRef = useRef<CallbackType<T> | undefined>(callback);
  const elementRef = useRef<T>(null);
  const [intersectedElement, setIntersectedElement] = useState<T | null>(null);
  const hasTriggeredRef = useRef<boolean>(false);
  const [hasTriggered, setHasTriggered] = useState<boolean>(false);
  const observerRef = useRef<IntersectionObserver>(null);
  const optionsRef = useRef<IntersectionObserverInit>(options);

  const createObserver = (newOptions: IntersectionObserverInit) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!once || !hasTriggeredRef.current) {
            void callbackRef.current?.(elementRef, hasTriggeredRef.current);
          }
          hasTriggeredRef.current = true;
          setHasTriggered(true);
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
        ...newOptions,
      }
    );

    observerRef.current = observer;

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
  };

  const intersectionRef = (node: T | null) => {
    if (observerRef.current && elementRef.current) {
      observerRef.current.unobserve(elementRef.current);
    }

    elementRef.current = node;
    setIntersectedElement(node);

    if (node) {
      createObserver(optionsRef.current);
    }
  };

  const updateOptions = (newOptions: IntersectionObserverInit) => {
    if (
      newOptions.root !== optionsRef.current.root ||
      newOptions.rootMargin !== optionsRef.current.rootMargin ||
      newOptions.threshold !== optionsRef.current.threshold
    ) {
      if (newOptions.root) {
        hasTriggeredRef.current = false;
        setHasTriggered(false);
      }

      optionsRef.current = newOptions;
      createObserver(newOptions);
    }
  };

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return {
    intersectedElement,
    intersectionRef,
    triggered: hasTriggered,
    updateOptions,
  };
};

export default useIntersection;
